import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images, Trash2, ArrowRight, Lock } from 'lucide-react';

import { useApi } from '../api-context';
import { ProgressBar, Spinner } from '../components/Primitives';
import ContactSheet from '../components/ContactSheet';
import ConfirmDialog from '../components/ConfirmDialog';
import SiteNav from '../components/site/SiteNav';
import {
  HeroFrame,
  Aftermath,
  Suggests,
  Experience,
  Privacy,
  Price,
  Maker,
  WhoFor,
  FinalCta,
  SiteOutro,
  SiteFooter,
} from '../components/site/Sections';
import type { MediaItem, Project, ScanProgress } from '../../types';

const COVER_COUNT = 5;

export default function Home() {
  const api = useApi();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [covers, setCovers] = useState<Record<number, MediaItem[]>>({});
  const [photos, setPhotos] = useState<string[]>([]);
  const [scan, setScan] = useState<ScanProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

  const refresh = useCallback(() => {
    const list = api.listProjects();
    setProjects(list);
    setCovers(
      Object.fromEntries(
        list.map((project) => [
          project.id,
          api.listMedia(project.id, { limit: COVER_COUNT, offset: 0, filter: 'all' }).items,
        ]),
      ),
    );
  }, [api]);

  useEffect(refresh, [refresh]);

  // Real photographs carry the page, so they are loaded once and shared.
  useEffect(() => {
    let cancelled = false;
    void api.sampleShootUrls(14).then((urls) => {
      if (!cancelled) setPhotos(urls);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const startImport = async (
    run: () => Promise<{ project: Project; added: number } | null>,
    nothingFound: string,
  ) => {
    setError(null);
    setBusy(true);
    try {
      const result = await run();
      if (!result) return; // dialog dismissed

      if (result.added === 0) {
        setError(nothingFound);
        api.deleteProject(result.project.id);
        refresh();
        return;
      }
      // The shoot page, not the culling screen. It is the only screen that
      // offers the review, reports its progress and shows what it produced —
      // and with a review on the table, dropping someone straight into manual
      // culling makes the choice for them.
      navigate(`/p/${result.project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setScan(null);
    }
  };

  const importFolder = () =>
    startImport(() => api.importFolder(setScan), 'That folder has no photos or videos in it.');

  const importFiles = () =>
    startImport(
      () => api.importFiles(setScan),
      'None of those files are photos or videos m3cull can read.',
    );

  const importDemo = async () => {
    setBusy(true);
    try {
      const result = await api.importDemo(setScan);
      // The demo keeps going straight to the culling screen. Its whole job is
      // to put something moving on screen as fast as possible for someone who
      // has not decided yet; a shoot page in between is a click that costs
      // exactly that.
      navigate(`/p/${result.project.id}/review`);
    } finally {
      setBusy(false);
      setScan(null);
    }
  };

  const remove = (project: Project) => {
    api.deleteProject(project.id);
    setPendingDelete(null);
    refresh();
  };

  return (
    <div id="top" className="flex min-h-full flex-col">
      <SiteNav
        onTry={importFolder}
        busy={busy}
        shootCount={projects.length}
        onShoots={() => navigate('/shoots')}
      />

      {/* ================= hero ================= */}
      {/*
        The opening line is the photographer's, not the product's. Every tool in
        this category opens by describing itself; the one thing a photographer
        already agrees with before reading a word is that the moment does not
        come back. The product claim goes directly underneath it, in the same
        breath, so nobody has to scroll to find out what this is.
      */}
      <section id="main" className="mx-auto w-full max-w-content px-5 pb-16 pt-10 sm:px-8 sm:pt-16 lg:pb-24 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-16">
          <div className="flex flex-col gap-7">
            <p className="label-mono">Photo culling · free · in your browser</p>

            <h1 className="max-w-xl text-mega font-semibold text-balance text-ink-50">
              You shoot more because the moment will not happen twice.
            </h1>

            <p className="max-w-lg text-sub text-ink-300">
              m3cull is the part that comes after: cull 3,000 photos in 30 minutes, in a browser
              tab, and decide every one of them yourself.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button
                className="btn-primary px-6 py-3 text-body"
                onClick={importFolder}
                disabled={busy}
              >
                {busy && !scan ? <Spinner className="h-4 w-4" /> : null}
                Start Culling
              </button>
              <button
                className="btn-outline group px-6 py-3 text-body"
                onClick={importDemo}
                disabled={busy}
              >
                Try a sample shoot
                <ArrowRight size={15} className="transition group-hover:translate-x-0.5" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-ink-850 pt-6 text-caption">
              <span className="flex items-center gap-2 text-ink-400">
                <Lock size={13} className="shrink-0 text-keep" />
                Your photos never leave your machine.
              </span>
              <button
                className="flex items-center gap-2 text-ink-400 transition hover:text-ink-100"
                onClick={importFiles}
                disabled={busy}
              >
                <Images size={14} />
                Choose photos instead
              </button>
            </div>
          </div>

          <HeroFrame photos={photos} />
        </div>

        {scan && (
          <div className="card mt-10 flex items-center gap-3 p-4">
            <Spinner className="h-4 w-4 shrink-0 text-ink-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-caption text-ink-100">
                {scan.phase === 'walking' ? 'Reading' : 'Adding photos from'} {scan.sourceName}
              </p>
              <p className="tnum text-tiny text-ink-500">{scan.scanned} found so far</p>
            </div>
          </div>
        )}

        {error && (
          <p className="mt-10 rounded-lg border border-reject-line bg-reject-soft px-4 py-3 text-caption text-reject">
            {error}
          </p>
        )}
      </section>

      {/* ================= your shoots (returning visitors) ================= */}
      {projects.length > 0 && (
        <section className="mx-auto w-full max-w-content px-5 pb-12 sm:px-8">
          <div className="flex items-baseline justify-between border-b border-ink-850 pb-3">
            <h2 className="label-mono">Pick up where you left off</h2>
            <button
              className="flex items-center gap-1.5 text-caption text-ink-400 transition hover:text-ink-50"
              onClick={() => navigate('/shoots')}
            >
              All {projects.length} shoots
              <ArrowRight size={13} />
            </button>
          </div>

          <ul className="divide-y divide-ink-850">
            {projects.slice(0, 3).map((project) => {
              const left = project.itemCount - project.reviewedCount;
              return (
                <li key={project.id} className="group relative flex items-center gap-5 py-4">
                  <ContactSheet
                    items={covers[project.id] ?? []}
                    size="sm"
                    className="hidden shrink-0 sm:flex"
                  />

                  <button
                    className="min-w-0 flex-1 text-left"
                    onClick={() => navigate(`/p/${project.id}`)}
                  >
                    <span className="absolute inset-0" aria-hidden="true" />
                    <p className="truncate text-body font-medium text-ink-50">{project.name}</p>
                    <p className="tnum mt-0.5 flex flex-wrap gap-x-2 text-tiny text-ink-500">
                      <span>{project.itemCount} photos</span>
                      <span aria-hidden="true">·</span>
                      <span className={project.keptCount > 0 ? 'text-keep' : undefined}>
                        {project.keptCount} kept
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>{left === 0 ? 'all reviewed' : `${left} left`}</span>
                    </p>
                  </button>

                  <div className="hidden w-24 shrink-0 sm:block">
                    <ProgressBar value={project.reviewedCount} total={project.itemCount} />
                  </div>

                  <button
                    className="btn-ghost relative z-10 px-2 opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
                    onClick={() => setPendingDelete(project)}
                    aria-label={`Delete ${project.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                  <ArrowRight size={15} className="shrink-0 text-ink-600" aria-hidden="true" />
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ================= the story ================= */}
      {/*
        The pile first, the product second. Naming the problem before naming the
        thing that solves it is the difference between a page a photographer
        recognises and a page that opens with its own feature list.
      */}
      <Aftermath photos={photos} />
      <Suggests photos={photos} />
      <Experience />
      <Privacy />
      <Price />
      {/* The maker sits directly under the price, because he is the reason for it. */}
      <Maker photo={photos[1]} />
      <WhoFor photos={photos} />

      <FinalCta onStart={importFolder} onDemo={importDemo} busy={busy} />

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete “${pendingDelete.name}”?`}
          body="Only m3cull's record of the shoot goes. Your photographs stay exactly where they are, untouched."
          confirmLabel="Delete the shoot"
          onConfirm={() => remove(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      <SiteOutro />
      <SiteFooter />
    </div>
  );
}
